import { z } from 'zod';

/**
 * Minimalny DSL opisujący dozwolone wartości ustawienia.
 *
 * Cztery przypadki pokrywają cały katalog Cloudflare, którego dotykamy.
 * Wszystko inne (schemat Zod, typ TS, formularz pracownika) jest z tego
 * wyprowadzane — nigdy pisane drugi raz.
 */
export type ValueSpec =
  | { readonly t: 'enum'; readonly values: readonly [string, ...string[]] }
  | { readonly t: 'intEnum'; readonly values: readonly [number, ...number[]] }
  | { readonly t: 'bool' }
  | { readonly t: 'expression' };

/** Typ wartości wynikający ze specyfikacji. */
export type InferValue<S extends ValueSpec> = S extends {
  t: 'enum';
  values: readonly (infer V extends string)[];
}
  ? V
  : S extends { t: 'intEnum'; values: readonly (infer V extends number)[] }
    ? V
    : S extends { t: 'bool' }
      ? boolean
      : S extends { t: 'expression' }
        ? string
        : never;

/**
 * Schemat ZAPISU — ścisły. Odrzuca wszystko spoza dozwolonych wartości.
 * Używany przy zapisie presetu i przed wysłaniem czegokolwiek do Cloudflare.
 */
export function buildWriteSchema<S extends ValueSpec>(spec: S): z.ZodType<InferValue<S>> {
  switch (spec.t) {
    case 'enum':
      return z.enum([...spec.values] as [string, ...string[]]) as unknown as z.ZodType<
        InferValue<S>
      >;
    case 'intEnum': {
      const allowed: readonly number[] = spec.values;
      return z
        .number()
        .int()
        .refine((v) => allowed.includes(v), {
          message: `Dozwolone wartości: ${allowed.join(', ')}.`,
        }) as unknown as z.ZodType<InferValue<S>>;
    }
    case 'bool':
      return z.boolean() as unknown as z.ZodType<InferValue<S>>;
    case 'expression':
      return z.string().min(1, 'Wyrażenie nie może być puste.') as unknown as z.ZodType<
        InferValue<S>
      >;
  }
}

/** Czy wartość mieści się w specyfikacji — bez rzucania wyjątku. */
export function isAllowedValue(spec: ValueSpec, value: unknown): boolean {
  switch (spec.t) {
    case 'enum':
      return typeof value === 'string' && (spec.values as readonly string[]).includes(value);
    case 'intEnum':
      return typeof value === 'number' && (spec.values as readonly number[]).includes(value);
    case 'bool':
      return typeof value === 'boolean';
    case 'expression':
      return typeof value === 'string' && value.length > 0;
  }
}

/** Wszystkie dozwolone wartości — do zbudowania selecta w formularzu pracownika. */
export function allowedValues(spec: ValueSpec): readonly (string | number | boolean)[] {
  switch (spec.t) {
    case 'enum':
    case 'intEnum':
      return spec.values;
    case 'bool':
      return [true, false];
    case 'expression':
      return [];
  }
}
